import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { InputType } from '../../../models/input-type.model';
import { QuestionType } from '../../../models/question-type.model';

@Component({
  selector: `lib-input-container`,
  templateUrl: './input-container.component.html',
  styleUrls: ['./input-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InputContainerComponent implements AfterViewInit, OnChanges {
  @Input() headLine: string;
  @Input() questionMarkText: string;
  @Input() label: string;
  @Input() labelPrefix: string;
  @Input() labelHeader: boolean;
  @Input() for: string;
  @Input() description: string;
  @Input() fileOrEndDateDescription: string;
  @Input() required: boolean;
  @Input() markRequired: boolean;
  helpText = input<string>();
  errorText = input<string>();
  warningText = input<string>();
  @Input() inputType: InputType;
  @Input() questionType: QuestionType;
  currentLength = input<number>();
  @Input() isNumber: boolean;
  maxLength = input<number | undefined>();
  @Input() width: 'small' | 'medium' | 'large' | 'full';
  @Input() autoWidth: boolean;
  @Input() requiredTextEnd = true;
  @Input() id: string;
  @Input() hideAbove: boolean;
  @Input() hideBelow: boolean;
  @Input() showChangeIndicator: boolean;

  @ViewChild('inputcontentContainer') inputContainerRef: ElementRef;

  InputType = InputType;
  QuestionType = QuestionType;
  constructor(private zone: NgZone) {}

  ngAfterViewInit() {
    this.setWidth();
    this.setOptionalStyle();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['maxLength'] && !changes['maxLength'].firstChange) ||
      (changes['inputType'] && !changes['inputType'].firstChange) ||
      (changes['width'] && !changes['width'].firstChange)
    ) {
      this.setWidth();
    }

    if (changes['errorText']) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.setAriaValidation();
        });
      });
    }

    if (changes['warningText']) {
      this.setAriaValidation();
    }
  }
  private setWidth() {
    if (!this.inputContainerRef) {
      return;
    }

    this.inputContainerRef.nativeElement.classList.remove(
      'width-small',
      'width-medium',
      'width-large',
      'width-full',
    );

    if (this.width) {
      this.inputContainerRef.nativeElement.classList.add(`width-${this.width}`);
      return;
    }

    if (!this.autoWidth) {
      return;
    }

    const maxLength = this.maxLength();
    if (this.isShortNumericInput()) {
      this.inputContainerRef.nativeElement.classList.add('width-medium');

      this.inputContainerRef.nativeElement
        .querySelector('.input-above')
        .classList.add('above-small');

      this.inputContainerRef.nativeElement
        .querySelector('.required')
        ?.classList.add('width-small');

      this.inputContainerRef.nativeElement
        .querySelector('.input-content')
        .classList.add('width-small');

      this.inputContainerRef.nativeElement
        .querySelector('.input-below')
        .classList.add('width-small');
    } else if (
      (maxLength !== undefined && maxLength < 50) ||
      (this.inputType !== InputType.Text && this.inputType !== undefined)
    ) {
      this.inputContainerRef.nativeElement.classList.add('width-medium');
    } else {
      this.inputContainerRef.nativeElement.classList.add('width-full');
    }
  }

  private isShortNumericInput(): boolean {
    const maxLength = this.maxLength();
    return (
      maxLength !== undefined &&
      maxLength <= 4 &&
      (this.inputType === InputType.Amount ||
        this.inputType === InputType.Percent ||
        this.inputType === InputType.Number)
    );
  }

  private setOptionalStyle() {
    if (
      this.questionType === QuestionType.Checkbox ||
      this.questionType === QuestionType.RadioButton
    ) {
      const inputAboveElement =
        this.inputContainerRef?.nativeElement.querySelector('.input-above');
      if (inputAboveElement) {
        inputAboveElement.style.display = 'block';
        const requiredOrOptionalElement =
          inputAboveElement.querySelector('.required');
        if (requiredOrOptionalElement) {
          requiredOrOptionalElement.style.marginTop = '0.3rem';
          requiredOrOptionalElement.style.marginBottom = '0.3rem';
        }
      }
    }
  }

  private setAriaValidation() {
    const el = this.inputContainerRef?.nativeElement.querySelector(
      '.vinnova-fc, .vinnova-textarea, lib-vinnova-dropdown:not(.country-code-dropdown) input, [role="group"], [role="radiogroup"]',
    );
    if (!el) {
      return;
    }

    if (this.errorText()) {
      el.setAttribute('aria-invalid', true);
      el.setAttribute('aria-description', this.errorText());
    } else if (this.warningText()) {
      el.setAttribute('aria-description', this.warningText());
    } else {
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-description');
    }
  }
}
